import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useInvokeWorkflow } from '../../../api/workflows';
import { useAuth } from '../../../hooks/useAuth';
import type { LTWorkflowConfig } from '../../../api/types';
import {
  DEFAULT_ENVELOPE,
  extractDataFields,
  dataToFields,
  fieldsToJson,
} from './helpers';
import { IdentitySummary } from './IdentitySummary';
import { EnvelopeEditor } from './EnvelopeEditor';

export function StartNowPanel({ selected, executionsPath }: { selected: LTWorkflowConfig; executionsPath: string }) {
  const navigate = useNavigate();
  const { isSuperAdmin, hasRoleType } = useAuth();
  const isAdmin = isSuperAdmin || hasRoleType('admin');
  const invokeMutation = useInvokeWorkflow();
  const [jsonInput, setJsonInput] = useState(DEFAULT_ENVELOPE);
  const [parseError, setParseError] = useState('');
  const [formFields, setFormFields] = useState<Record<string, unknown>>({});
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [overrideBot, setOverrideBot] = useState('');
  const isCertifiable = (selected.roles?.length ?? 0) > 0 || (selected.consumes?.length ?? 0) > 0;
  const [certified, setCertified] = useState(isCertifiable);

  const dataFields = useMemo(
    () => extractDataFields(selected.envelope_schema ?? null),
    [selected.envelope_schema],
  );
  const hasFormView = dataFields.length > 0;

  const schemaMetadata = useMemo(() => {
    if (!selected.envelope_schema) return {};
    const md = selected.envelope_schema.metadata;
    return md && typeof md === 'object' ? (md as Record<string, unknown>) : {};
  }, [selected.envelope_schema]);

  useEffect(() => {
    setParseError('');
    invokeMutation.reset();

    const prefill = sessionStorage.getItem('lt:invoke:prefill');
    if (prefill) {
      sessionStorage.removeItem('lt:invoke:prefill');
      setJsonInput(prefill);
      try {
        const parsed = JSON.parse(prefill);
        const data = parsed?.data ?? parsed;
        if (data && typeof data === 'object') setFormFields(dataToFields(data));
      } catch { /* use as-is */ }
      setIsJsonMode(true);
      setOverrideBot('');
      return;
    }

    const json = selected.envelope_schema
      ? JSON.stringify(selected.envelope_schema, null, 2)
      : DEFAULT_ENVELOPE;
    setJsonInput(json);
    if (selected.envelope_schema?.data && typeof selected.envelope_schema.data === 'object') {
      setFormFields(dataToFields(selected.envelope_schema.data as Record<string, unknown>));
    } else {
      setFormFields({});
    }
    setIsJsonMode(!extractDataFields(selected.envelope_schema ?? null).length);
    setOverrideBot('');
    setCertified(isCertifiable);
  }, [selected.workflow_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleMode = () => {
    if (isJsonMode) {
      try {
        const parsed = JSON.parse(jsonInput);
        if (parsed.data && typeof parsed.data === 'object') setFormFields(dataToFields(parsed.data));
      } catch { /* keep existing */ }
    } else {
      setJsonInput(fieldsToJson(formFields, schemaMetadata));
    }
    setIsJsonMode(!isJsonMode);
  };

  const updateFormField = (key: string, value: unknown, type: string) => {
    let parsed = value;
    if (type === 'number') parsed = value === '' ? 0 : Number(value);
    else if (type === 'boolean') parsed = value === 'true' || value === true;
    const updated = { ...formFields, [key]: parsed };
    setFormFields(updated);
    setJsonInput(fieldsToJson(updated, schemaMetadata));
  };

  const handleInvoke = async () => {
    setParseError('');
    let envelope: Record<string, unknown>;
    try {
      envelope = JSON.parse(jsonInput);
    } catch {
      setParseError('Invalid JSON');
      return;
    }
    const { data, metadata } = envelope;
    if (!data || typeof data !== 'object') {
      setParseError('Envelope must include a "data" object');
      return;
    }
    try {
      const resolvedMetadata = { ...((metadata as Record<string, unknown>) ?? {}) };
      if (certified) resolvedMetadata.certified = true;
      await invokeMutation.mutateAsync({
        workflowType: selected.workflow_type,
        data: data as Record<string, unknown>,
        metadata: resolvedMetadata,
        ...(overrideBot ? { execute_as: overrideBot } : {}),
      });
      navigate(executionsPath);
    } catch { /* error via mutation */ }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-mono font-medium text-text-primary">{selected.workflow_type}</h2>
        {selected.description && (
          <p className="text-xs text-text-quaternary mt-1 leading-relaxed">
            {selected.description}
          </p>
        )}
      </div>

      <IdentitySummary
        config={selected}
        overrideBot={overrideBot}
        onOverrideChange={setOverrideBot}
        showOverride={isAdmin}
      />

      {isCertifiable && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={certified}
            onChange={(e) => setCertified(e.target.checked)}
            className="rounded border-surface-border text-accent focus:ring-accent/30"
          />
          <ShieldCheck className="w-3.5 h-3.5 text-text-tertiary" />
          <span className="text-xs text-text-secondary">Enable task tracking and escalation routing</span>
        </label>
      )}

      <EnvelopeEditor
        selectedConfig={selected}
        isJsonMode={isJsonMode}
        hasFormView={hasFormView}
        jsonInput={jsonInput}
        formFields={formFields}
        dataFields={dataFields}
        onJsonChange={(v) => { setJsonInput(v); setParseError(''); }}
        onToggleMode={handleToggleMode}
        onUpdateFormField={updateFormField}
        onSetFormFields={setFormFields}
      />

      {parseError && <p className="text-xs text-status-error">{parseError}</p>}
      {invokeMutation.error && <p className="text-xs text-status-error">{invokeMutation.error.message}</p>}
      {invokeMutation.isSuccess && <p className="text-xs text-status-success">Workflow started</p>}

      <button onClick={handleInvoke} disabled={invokeMutation.isPending} className="btn-primary">
        {invokeMutation.isPending ? 'Starting...' : 'Start Workflow'}
      </button>
    </div>
  );
}
